import { Box, Button, List, Pagination, Typography } from "@mui/material"
import { OpenDraft } from "event-definitions"
import { PropertyBox } from "lib/@components/property-box"
import { ListItemBox } from "lib/@components/ListItemBox"
import { prevent } from "lib/prevent"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { setFromValueParam } from "lib/setFromEvent"
import { useReference, useReferenceState } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useDialog } from "lib/@hooks/useDialog"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { createNewSchedule } from "routes/schedule/my-schedules/my-custom-schedules/controller/create-new-schedule"
import { SortComponent } from "routes/schedule/my-schedules/my-custom-schedules/my-list"
import { MyScheduleSort } from "routes/schedule/my-schedules/my-custom-schedules/my-schedule-sort"
import { NewScheduleDialog } from "routes/schedule/my-schedules/my-custom-schedules/new-schedule-dialog"
import { useSelectSchedule } from "routes/schedule/my-schedules/my-custom-schedules/select-schedule"
import { useMySchedules } from "routes/schedule/my-schedules/my-custom-schedules/lib/use-my-schedules"
import { MyScheduleListItem } from "slot-definitions"
import { Secure } from "lib/authorization/secure"
import { Summaries } from "routes/schedule/plugins/custom-summaries/summaries"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { CreateDraft } from "routes/schedule/my-schedules/my-custom-schedules/create-draft"
import { ContextMenu } from "lib/@components/context-menu-bar"
import { RemoveDraft } from "routes/schedule/my-schedules/my-custom-schedules/remove-draft"
import { ReinstateRetired } from "routes/schedule/my-schedules/my-custom-schedules/reinstate-retired"
import { DeleteRetired } from "routes/schedule/my-schedules/my-custom-schedules/delete-retired"
import { ContextMenuItem } from "lib/@components/context-menu-bar/context-menu-item"
import { retireSchedules } from "routes/schedule/my-schedules/my-custom-schedules/controller/retire-schedules"
import { useSelectedId } from "routes/schedule/lib/useSelectedId"
import { filterSchedules } from "ioc"
import { Frag } from "lib/@components/slot/frag"
import Iconify from "minimals-template/components/Iconify"
import { useEffect } from "react"
import { useMobile } from "lib/@components/mobile"
import { useOptionChooser } from "lib/@dialogs/option-chooser"
import { PageSize } from "lib/@components/pagination/page-size"
import { busyWhile } from "lib/@components/busy/busy-while"
import { getOriginatingSchedule } from "routes/schedule/plugins/custom-schedule-from-tailored/lib/get-originating-schedule.js"
import { SelectAllSchedules } from "routes/schedule/my-schedules/my-custom-schedules/select-all-schedules"
import { useSelectTailoredSchedule } from "routes/schedule/plugins/custom-schedule-from-tailored/select-tailored-schedule.js"
import { FEATURE_FLAG_CUSTOM_SCHEDULE_FROM_TAILORED } from "routes/schedule/plugins/custom-schedule-from-tailored/feature-flag-custom-schedule-from-tailored.mjs"
import { isEnabled } from "lib/@components/feature"
import { createTailoredCopy } from "routes/schedule/plugins/custom-schedule-from-tailored/controller/create-tailored-copy.controller.js"
import { errorSnackbar } from "lib/snackbar/error-snackbar.js"

const PAGE_SIZE = 10

const typeOptions = {
    derived: {
        icon: "carbon:parent-child",
        title: "Derived schedule",
        description:
            "You may derive a schedule and maintain a relationship with the parent.  You will start off with all of the parent content and you can make any updates you like. If the parent is updated you will be notified and will be able to quickly accept or edit changes",
    },
    custom: {
        icon: "dashicons:admin-customizer",
        title: "Custom schedule",
        description: "Your schedule will be unique and start off blank, it will not receive automatic updates",
    },
    tailoredCopy: {
        // schedules/my-schedules/plugins/custom-schedule-from-tailored
        icon: "carbon:document-export",
        title: "Derive from tailored schedule",
        description:
            "Create a derived custom schedule from the original schedule, with the selected tailored changes applied.",
    },
}

export function ListOfMySchedules({ drafts, published, retired = false, pageSize: initialPageSize = PAGE_SIZE }) {
    const [{ myScheduleSort: sortMode }, setReference] = useReference(
        { myScheduleSort: "modified_descending" },
        "myScheduleSort"
    )
    const [pageSize, setPageSize] = useReferenceState(`custom-schedules-general-page-size`, initialPageSize)
    const isMobile = useMobile()
    const { disableCreation } = useBoundContext()
    const options = useOptionChooser(availableTypeOptions(), "Choose the type of schedule you want to create")
    const [page, setPage] = useSearchParam("page", 1, (v) => +v)
    const [search] = useSearchParam("search", "")
    const [mySchedules, loading] = useMySchedules({
        sortMode,
        drafts,
        published,
        retired,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        search,
    })
    const lastPage = Math.ceil(mySchedules?.count / pageSize)
    const showPaginator = mySchedules?.count > 4
    if (lastPage < page && lastPage !== 0) {
        setPage(lastPage)
    }

    useEffect(() => {
        setPage(1)
    }, [pageSize, setPage])

    const chooseSchedule = useSelectSchedule()
    const chooseTailored = useSelectTailoredSchedule()
    const setupSchedule = useDialog(<NewScheduleDialog />, { maxWidth: "sm", fullScreen: false })

    if (!loading && !mySchedules) return null
    return loading ? (
        <LoadingScreen />
    ) : (
        <PropertyBox sx={{ mb: 6 }}>
            <Secure demand="scheduleEditor" hide>
                <ContextMenu.Plug type="published">
                    <CreateDraft priority={200} />
                    <Retire priority={250} />
                </ContextMenu.Plug>
                <ContextMenu.Plug type="draft">
                    <RemoveDraft />
                </ContextMenu.Plug>
                <ContextMenu.Plug type="retired">
                    <ReinstateRetired priority={200} />
                    <DeleteRetired priority={250} />
                </ContextMenu.Plug>
            </Secure>

            <Typography component={ListItemBox} variant="subtitle1" data-cy="custom-schedules-count">
                <Box flex={1} flexBasis="100%">
                    Schedules{" "}
                    <Typography component="span" variant="body2" color="text.secondary">
                        ({mySchedules.count})
                    </Typography>
                </Box>
            </Typography>
            <Summaries />
            <SearchBoxUsingSearchParam />
            {!disableCreation && (
                <Secure hide demand="scheduleEditor">
                    <ListItemBox>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={prevent(create)}
                            startIcon={<Iconify icon={"ic:round-add"} />}
                            data-cy="create-schedule-button"
                        >
                            Create Schedule
                        </Button>

                        <Box flex={1} />
                    </ListItemBox>
                </Secure>
            )}
            <SortComponent>
                <MyScheduleSort value={sortMode} onChange={(mode) => setReference({ myScheduleSort: mode })} />
            </SortComponent>

            <Paginator
                isMobile={isMobile}
                pageSize={pageSize}
                setPageSize={setPageSize}
                setPage={setPage}
                mySchedules={mySchedules}
                page={page}
                showPaginator={showPaginator}
            />

            <List sx={{ mt: 0 }} data-cy="my-schedules-list">
                <SelectAllSchedules drafts={drafts} published={published} retired={retired} />
                {filterSchedules(mySchedules.scheduleItems).map((schedule) => (
                    <MyScheduleListItem.Slot
                        Container={<Frag />}
                        key={schedule._id}
                        target={schedule}
                        type={schedule.itemType}
                        schedule={schedule}
                    />
                ))}
            </List>

            {!isMobile && (
                <Paginator
                    isMobile={isMobile}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setPage={setPage}
                    mySchedules={mySchedules}
                    page={page}
                    showPaginator={showPaginator}
                />
            )}
        </PropertyBox>
    )

    // no slot to plug the new option into, so just using flag to gate it here
    function availableTypeOptions() {
        if (isEnabled(FEATURE_FLAG_CUSTOM_SCHEDULE_FROM_TAILORED)) return typeOptions

        return Object.fromEntries(Object.entries(typeOptions).filter(([type]) => type !== "tailoredCopy"))
    }

    async function create() {
        const type = await options()
        if (type === "derived") {
            const schedule = await chooseSchedule()
            if (schedule) {
                const definition = await setupSchedule({
                    originatingScheduleId: schedule._id,
                    originatingScheduleCode: schedule.code,
                })
                if (definition) {
                    const newDraft = await createNewSchedule(definition)
                    OpenDraft(newDraft).raise(newDraft)
                }
            }
        } else if (type === "tailoredCopy") {
            const { scheduleNodeId, treeId, scheduleId } = (await chooseTailored()) ?? {}
            if (!!scheduleNodeId && !!treeId) {
                // need to acquire originatingId/code data which isn't present on the regime tree schedule node
                const source = await getOriginatingSchedule(scheduleId)
                const definition = await setupSchedule(source)
                if (definition) {
                    try {
                        const newDraft = await createTailoredCopy({ definition, scheduleNodeId, treeId })
                        OpenDraft(newDraft).raise(newDraft)
                    } catch (error) {
                        errorSnackbar("Could not create tailored copy")
                    }
                }
            }
        } else if (type) {
            const definition = await setupSchedule()
            if (definition) {
                const newDraft = await createNewSchedule(definition)
                OpenDraft(newDraft).raise(newDraft)
            }
        }
    }
}

function Paginator({ isMobile, pageSize, setPageSize, setPage, mySchedules, page, showPaginator }) {
    return (
        showPaginator && (
            <ListItemBox data-cy="custom-schedules-paginator">
                <Box flex={1} />
                <PageSize take={pageSize} setTake={setPageSize} />
                <Pagination
                    size={isMobile ? "large" : "small"}
                    onChange={setFromValueParam((v) => {
                        setPage(v)
                    })}
                    count={Math.ceil(mySchedules.count / pageSize)}
                    color="primary"
                    page={page}
                />
            </ListItemBox>
        )
    )
}

export function Retire() {
    const { target } = useBoundContext()
    const [isSelected, toggle] = useSelectedId([target._id, target.id])
    return <ContextMenuItem icon="mdi:bed" label="Retire" onClick={retire} data-cy="menu-item-retire" />

    async function retire() {
        if (isSelected) {
            toggle()
        }
        await busyWhile(() => retireSchedules(true, [target._id]), "Retiring")
    }
}
